import {
    Button,
    Checkbox,
    Combobox,
    Drawer,
    DrawerBody,
    DrawerHeader,
    DrawerHeaderTitle,
    Dropdown,
    makeStyles,
    Option,
    Textarea,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import React, { useCallback, useEffect, useState } from 'react';
import type { ProjectUserInfo } from '../models/ProjectUserInfo';
import type { ProjektArtItem } from '../models/ProjektArtItem';
import type { ProjektInstitutItem } from '../models/ProjektInstitutItem';
import type { ProjektItem } from '../models/ProjektItem';
import type { ProjektKontaktItem } from '../models/ProjektKontaktItem';
import type { ProjektPartnerItem } from '../models/ProjektPartnerItem';
import type { DataService } from '../services/dataService';

const useStyles = makeStyles({
    drawerContent: {
        padding: '20px',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
    },
    formGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
    },
    formLabel: {
        fontWeight: 600,
        fontSize: '14px',
    },
    ndaSection: {
        padding: '12px',
        backgroundColor: '#f3f2f1',
        borderRadius: '4px',
        borderLeft: '4px solid #0078d4',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        fontSize: '13px',
        lineHeight: '1.5',
    },
    ndaLink: {
        color: '#0078d4',
        textDecoration: 'none',
        fontWeight: 600,
        cursor: 'pointer',
        '&:hover': {
            textDecoration: 'underline',
        },
    },
    buttonGroup: {
        display: 'flex',
        gap: '8px',
        marginTop: '20px',
    },
});

interface ProjectCreateDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    userInfo: ProjectUserInfo | null;
    dataService: DataService;
    onProjectCreated?: (projektId: number) => void;
}

const ProjectCreateDrawer: React.FC<ProjectCreateDrawerProps> = ({
    isOpen,
    onClose,
    userInfo,
    dataService,
    onProjectCreated,
}) => {
    const styles = useStyles();

    // Form state
    const [titleShort, setTitleShort] = useState('');
    const [title, setTitle] = useState('');
    const [projectPartner, setProjectPartner] = useState('');
    const [projectPartnerId, setProjectPartnerId] = useState<number | null>(null);
    const [contactPerson, setContactPerson] = useState('');
    const [contactPersonId, setContactPersonId] = useState<number | null>(null);
    const [projectDescription, setProjectDescription] = useState('');
    const [institute, setInstitute] = useState('');
    const [instituteId, setInstituteId] = useState<number | null>(null);
    const [projectType, setProjectType] = useState('');
    const [projectTypeId, setProjectTypeId] = useState<number | null>(null);
    const [other, setOther] = useState('');
    const [onlyVisibleForTeam, setOnlyVisibleForTeam] = useState(false);

    // Dropdown options state
    const [partnerSearchResults, setPartnerSearchResults] = useState<ProjektPartnerItem[]>([]);
    const [contactPersons, setContactPersons] = useState<ProjektKontaktItem[]>([]);
    const [institutes, setInstitutes] = useState<ProjektInstitutItem[]>([]);
    const [projectTypes, setProjectTypes] = useState<ProjektArtItem[]>([]);
    const [isLoadingOptions, setIsLoadingOptions] = useState(false);
    const [searchTimeout, setSearchTimeout] = useState<ReturnType<typeof setTimeout> | null>(null);
    const [isCreating, setIsCreating] = useState(false);
    const [createError, setCreateError] = useState<string | null>(null);

    // Handlers for TextFields
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleTextFieldChange = (setter: (value: string) => void) => (_ev: any, data: any) => {
        if (data && data.value !== undefined) {
            setter(data.value);
        }
    };

    // Handler for Textarea
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleTextAreaChange = (setter: (value: string) => void) => (_ev: any, data: any) => {
        if (data && data.value !== undefined) {
            setter(data.value);
        }
    };

    // Handler for Checkbox
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleCheckboxChange = (setter: (value: boolean) => void) => (_ev: any, data: any) => {
        if (data && typeof data.checked === 'boolean') {
            setter(data.checked);
        }
    };

    // Partner search handler with debouncing
    const handlePartnerSearch = useCallback((searchTerm: string) => {
        setProjectPartner(searchTerm);

        // Clear previous timeout
        if (searchTimeout) {
            clearTimeout(searchTimeout);
        }

        // Only search if 3 or more characters
        if (searchTerm.length >= 3) {
            const timeout = setTimeout(async () => {
                try {
                    const results = await dataService.getProjektPartnerSearch(searchTerm);
                    setPartnerSearchResults(results);
                } catch (error) {
                    console.error('Error searching partners:', error);
                    setPartnerSearchResults([]);
                }
            }, 300); // 300ms debounce
            setSearchTimeout(timeout);
        } else {
            setPartnerSearchResults([]);
        }
    }, [dataService, searchTimeout]);

    // Handler for partner selection
    const handlePartnerSelect = useCallback((partnerId: number) => {
        setProjectPartnerId(partnerId);
        // Fetch contact persons for selected partner
        const fetchContacts = async () => {
            try {
                const contacts = await dataService.getProjektKontakte(partnerId);
                setContactPersons(contacts);
            } catch (error) {
                console.error('Error fetching contacts:', error);
                setContactPersons([]);
            }
        };
        fetchContacts();
    }, [dataService]);

    // Fetch institutes and project types
    useEffect(() => {
        const fetchOptions = async () => {
            setIsLoadingOptions(true);
            try {
                // Fetch institutes
                const instituteData = await dataService.getProjektInstitute();
                setInstitutes(instituteData);

                // Fetch project types - use userInfo.departmentId
                if (userInfo?.departmentId) {
                    const typeData = await dataService.getProjektArten(userInfo.departmentId);
                    setProjectTypes(typeData);
                }
            } catch (error) {
                console.error('Error fetching options:', error);
            } finally {
                setIsLoadingOptions(false);
            }
        };

        if (isOpen) {
            fetchOptions();
        }
    }, [isOpen, dataService, userInfo?.departmentId]);

    // Reset form when drawer closes
    useEffect(() => {
        if (!isOpen) {
            setTitleShort('');
            setTitle('');
            setProjectPartner('');
            setProjectPartnerId(null);
            setContactPerson('');
            setContactPersonId(null);
            setProjectDescription('');
            setInstitute('');
            setInstituteId(null);
            setProjectType('');
            setProjectTypeId(null);
            setOther('');
            setOnlyVisibleForTeam(false);
            setPartnerSearchResults([]);
            setContactPersons([]);
        }
    }, [isOpen]);

    const handleCreateProject = useCallback(async () => {
        try {
            setIsCreating(true);
            setCreateError(null);

            // Validate required fields
            if (!titleShort.trim() || !title.trim() || !instituteId || !projectTypeId) {
                setCreateError('Please fill in all required fields (Title Short, Title, Institute, Project Type)');
                return;
            }

            // Create project object
            const newProject = {
                projektID: 0, // Will be assigned by server
                projekttitelIntern: titleShort.trim(),
                projekttitelLangform: title.trim(),
                projektKurzbeschreibung: projectDescription.trim(),
                auftraggeberID: projectPartnerId || undefined,
                ansprechpartnerID: contactPersonId || undefined,
                departmentID: instituteId,
                projektartID: projectTypeId,
                projektart: projectType.trim(),
                projektSonstigeAnmerkungen: other.trim(),
                ausgeblendet: onlyVisibleForTeam,
            } as unknown as ProjektItem;

            // Call API to create project
            const projektId = await dataService.addProjekt(newProject);

            console.log('Project created successfully with ID:', projektId);

            // Trigger callback if provided
            if (onProjectCreated) {
                onProjectCreated(projektId);
            }

            // Reset form
            setTitleShort('');
            setTitle('');
            setProjectPartner('');
            setProjectPartnerId(null);
            setContactPerson('');
            setContactPersonId(null);
            setProjectDescription('');
            setInstitute('');
            setInstituteId(null);
            setProjectType('');
            setProjectTypeId(null);
            setOther('');
            setOnlyVisibleForTeam(false);

            // Close drawer
            onClose();
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to create project';
            setCreateError(errorMessage);
            console.error('Error creating project:', error);
        } finally {
            setIsCreating(false);
        }
    }, [titleShort, title, projectDescription, projectPartnerId, contactPersonId, instituteId, projectTypeId, projectType, other, onlyVisibleForTeam, dataService, onProjectCreated, onClose]);

    return (
        <Drawer
            open={isOpen}
            onOpenChange={(_, { open }) => !open && onClose()}
            position="end"
            size="medium"
        >
            <DrawerHeader>
                <DrawerHeaderTitle
                    action={
                        <Button
                            appearance="subtle"
                            aria-label="Close"
                            icon={<Dismiss24Regular />}
                            onClick={onClose}
                        />
                    }
                >
                    Create a New Project
                </DrawerHeaderTitle>
            </DrawerHeader>
            <DrawerBody>
                <div className={styles.drawerContent}>
                    <div className={styles.formGroup}>
                        <label className={styles.formLabel}>Title (Short)</label>
                        <Textarea
                            placeholder="Short project title"
                            value={titleShort}
                            onChange={handleTextFieldChange(setTitleShort)}
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label className={styles.formLabel}>Title</label>
                        <Textarea
                            placeholder="Full project title"
                            value={title}
                            onChange={handleTextFieldChange(setTitle)}
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label className={styles.formLabel}>Contracting Entity (Projektpartner)</label>
                        <Combobox
                            placeholder="Type at least 3 letters to search..."
                            value={projectPartner}
                            onOptionSelect={(_, option) => {
                                const partnerId = parseInt(option.optionValue || '0');
                                const partner = partnerSearchResults.find(p => p.partnerID === partnerId);
                                if (partner) {
                                    setProjectPartner(partner.bezeichnung1);
                                    handlePartnerSelect(partnerId);
                                }
                            }}
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            onChange={(ev: any) => handlePartnerSearch((ev.target as HTMLInputElement).value)}
                        >
                            {partnerSearchResults.map((partner) => (
                                <Option key={partner.partnerID} value={partner.partnerID.toString()}>
                                    {partner.bezeichnung1}
                                </Option>
                            ))}
                        </Combobox>
                    </div>

                    <div className={styles.formGroup}>
                        <label className={styles.formLabel}>Contact Person (Ansprechpartner)</label>
                        <Dropdown
                            placeholder="Select contact person..."
                            value={contactPerson}
                            onOptionSelect={(_, option) => {
                                const contactId = parseInt(option.optionValue || '0');
                                const selectedContact = contactPersons.find(c => c.id === contactId);
                                if (selectedContact) {
                                    setContactPerson(selectedContact.name);
                                    setContactPersonId(selectedContact.id);
                                }
                            }}
                            disabled={!projectPartnerId || contactPersons.length === 0}
                        >
                            {contactPersons.map((contact) => (
                                <Option key={contact.id} value={contact.id.toString()}>
                                    {contact.name}
                                </Option>
                            ))}
                        </Dropdown>
                    </div>

                    <div className={styles.formGroup}>
                        <label className={styles.formLabel}>Project Description</label>
                        <Textarea
                            placeholder="Enter project description..."
                            rows={4}
                            value={projectDescription}
                            onChange={handleTextAreaChange(setProjectDescription)}
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <label className={styles.formLabel}>Institute / Department</label>
                        <Dropdown
                            placeholder="Select institute..."
                            value={institute}
                            onOptionSelect={(_, option) => {
                                const instituteIdValue = parseInt(option.optionValue || '0');
                                const selectedInstitute = institutes.find(i => i.id === instituteIdValue);
                                if (selectedInstitute) {
                                    setInstitute(selectedInstitute.bezeichnung);
                                    setInstituteId(selectedInstitute.id);
                                }
                            }}
                            disabled={isLoadingOptions}
                        >
                            {institutes.map((inst) => (
                                <Option key={inst.id} value={inst.id.toString()}>
                                    {inst.bezeichnung}
                                </Option>
                            ))}
                        </Dropdown>
                    </div>

                    <div className={styles.formGroup}>
                        <label className={styles.formLabel}>Project Type</label>
                        <Dropdown
                            placeholder="Select project type..."
                            value={projectType}
                            onOptionSelect={(_, option) => {
                                const typeId = parseInt(option.optionValue || '0');
                                const selectedType = projectTypes.find(t => t.projektartID === typeId);
                                if (selectedType) {
                                    setProjectType(selectedType.bezeichnung);
                                    setProjectTypeId(selectedType.projektartID);
                                }
                            }}
                            disabled={isLoadingOptions}
                        >
                            {projectTypes.map((type) => (
                                <Option key={type.projektartID} value={type.projektartID.toString()}>
                                    {type.bezeichnung}
                                </Option>
                            ))}
                        </Dropdown>
                    </div>

                    <div className={styles.formGroup}>
                        <label className={styles.formLabel}>Other</label>
                        <Textarea
                            placeholder="Additional information..."
                            value={other}
                            onChange={handleTextFieldChange(setOther)}
                        />
                    </div>

                    <div className={styles.formGroup}>
                        <Checkbox
                            label="Only visible for project team"
                            checked={onlyVisibleForTeam}
                            onChange={handleCheckboxChange(setOnlyVisibleForTeam)}
                        />
                    </div>

                    {createError && (
                        <div style={{
                            padding: '12px',
                            backgroundColor: '#fed9cc',
                            color: '#bc3b0a',
                            borderRadius: '4px',
                            fontSize: '14px',
                        }}>
                            {createError}
                        </div>
                    )}

                    <div className={styles.ndaSection}>
                        <div>
                            <strong>NDA Agreement</strong>
                        </div>
                        <div>
                            By creating this project, you automatically accept the{' '}
                            <a
                                href="https://localhost:5173/ndagreement?viewOnly=1"
                                target="_blank"
                                rel="noopener noreferrer"
                                className={styles.ndaLink}
                            >
                                NDA Agreement
                            </a>
                            .
                        </div>
                    </div>

                    <div className={styles.buttonGroup}>
                        <Button
                            appearance="primary"
                            onClick={handleCreateProject}
                            disabled={isCreating}
                        >
                            {isCreating ? 'Creating...' : 'Create Project'}
                        </Button>
                        <Button appearance="secondary" onClick={onClose} disabled={isCreating}>
                            Cancel
                        </Button>
                    </div>
                </div>
            </DrawerBody>
        </Drawer>
    );
};

export default ProjectCreateDrawer;
